import { useState, useEffect, useRef, useCallback } from 'react';

const CHATWOOT_BASE = process.env.REACT_APP_CHATWOOT_BASE_URL || 'https://chat.devcoski.tech';
const ACCOUNT_ID = process.env.REACT_APP_CHATWOOT_ACCOUNT_ID || '2';
// Usado só como fallback quando SSE está fora
const POLL_INTERVAL_FALLBACK = 60000;

const API = '/chatwoot-api';

// ============================================================
// MOCK — remover este bloco quando os tokens estiverem cadastrados
const CHATWOOT_MOCK = false;

const now = Math.floor(Date.now() / 1000);
const MOCK_CONVERSATIONS = {
  mine: [
    {
      id: 1001, status: 'pending',
      unread_count: 2,
      meta: { sender: { name: 'João Armador' } },
      last_non_activity_message: { content: 'Preciso liberar o container amanhã cedo, é possível?' },
      created_at: now - 300,
    },
    {
      id: 1002, status: 'open',
      unread_count: 0,
      meta: { sender: { name: 'Maria Transportes' } },
      last_non_activity_message: { content: 'Qual o status do agendamento?' },
      created_at: now - 1800,
    },
  ],
  team: [
    {
      id: 1003, status: 'open',
      unread_count: 1,
      meta: { sender: { name: 'Carlos Gate' } },
      last_non_activity_message: { content: 'Vistoria concluída, aguardando devolução.' },
      created_at: now - 7200,
    },
  ],
  resolved: [
    {
      id: 1004, status: 'resolved',
      unread_count: 0,
      meta: { sender: { name: 'Ana Logística' } },
      last_non_activity_message: { content: 'Tudo certo, obrigada!' },
      created_at: now - 86400,
    },
  ],
};

const MOCK_MESSAGES_DATA = {
  1001: [
    { id: 1, content: 'Olá! Precisamos liberar o container MSCU1234567 amanhã às 7h.', message_type: 0, created_at: now - 480, sender: { name: 'João Armador' } },
    { id: 2, content: 'Bom dia João! Vou verificar a disponibilidade de portão para esse horário.', message_type: 1, created_at: now - 420, sender: { name: 'Agente' } },
    { id: 3, content: 'Preciso liberar o container amanhã cedo, é possível?', message_type: 0, created_at: now - 300, sender: { name: 'João Armador' } },
  ],
  1002: [
    { id: 4, content: 'Boa tarde! Qual o status do agendamento de amanhã?', message_type: 0, created_at: now - 1800, sender: { name: 'Maria Transportes' } },
  ],
  1003: [
    { id: 5, content: 'A vistoria do container foi concluída.', message_type: 0, created_at: now - 8000, sender: { name: 'Carlos Gate' } },
    { id: 6, content: 'Perfeito, vou encaminhar para devolução.', message_type: 1, created_at: now - 7800, sender: { name: 'Agente' } },
    { id: 7, content: 'Vistoria concluída, aguardando devolução.', message_type: 0, created_at: now - 7200, sender: { name: 'Carlos Gate' } },
  ],
  1004: [
    { id: 8, content: 'Processo finalizado. Tudo certo, obrigada!', message_type: 0, created_at: now - 86400, sender: { name: 'Ana Logística' } },
  ],
};
// ============================================================

function getUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch {
    return {};
  }
}

async function fetchByStatus(status, token) {
  const res = await fetch(
    `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations?assignee_type=mine&status=${status}`,
    { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
  );
  if (!res.ok) return [];
  const json = await res.json();
  return json.data?.payload || [];
}

async function fetchTeamConvs(teamIds, token) {
  if (!teamIds?.length) return [];
  const results = await Promise.all(
    teamIds.flatMap(teamId => [
      fetch(`${API}/api/v1/accounts/${ACCOUNT_ID}/conversations?team_id=${teamId}&status=open`,
        { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } })
        .then(r => r.ok ? r.json() : {}).then(j => j.data?.payload || []),
      fetch(`${API}/api/v1/accounts/${ACCOUNT_ID}/conversations?team_id=${teamId}&status=pending`,
        { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } })
        .then(r => r.ok ? r.json() : {}).then(j => j.data?.payload || []),
    ])
  );
  // deduplicar por id
  const map = new Map();
  results.flat().forEach(c => map.set(c.id, c));
  return Array.from(map.values());
}

async function fetchMemberTeamIds(token) {
  const res = await fetch(
    `${API}/api/v1/accounts/${ACCOUNT_ID}/teams`,
    { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
  );
  if (!res.ok) return [];
  const teams = await res.json();
  return (Array.isArray(teams) ? teams : [])
    .filter(t => t.is_member)
    .map(t => t.id);
}

async function fetchTeamMembers(teamId, token) {
  const res = await fetch(
    `${API}/api/v1/accounts/${ACCOUNT_ID}/teams/${teamId}/team_members`,
    { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
  );
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// Retorna o id do primeiro time que contém o agente (busca em paralelo)
async function fetchAgentFirstTeamId(agentId, token) {
  const teamsRes = await fetch(
    `${API}/api/v1/accounts/${ACCOUNT_ID}/teams`,
    { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
  );
  if (!teamsRes.ok) return null;
  const teams = await teamsRes.json();
  if (!Array.isArray(teams) || teams.length === 0) return null;
  const results = await Promise.all(
    teams.map(team =>
      fetchTeamMembers(team.id, token).then(members => ({ teamId: team.id, members }))
    )
  );
  const found = results.find(({ members }) => members.some(m => m.id === agentId));
  return found ? found.teamId : null;
}

export function useChatwoot() {
  const [conversations, setConversations] = useState({ mine: [], team: [], resolved: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [newMessage, setNewMessage] = useState(null);
  const [agentAvailability, setAgentAvailability] = useState(null);
  // { conversationId, ts } — sinaliza nova mensagem via SSE para o offcanvas reagir diretamente
  const [liveConvUpdate, setLiveConvUpdate] = useState(null);
  // null = primeira carga (não dispara toast), {} = inicializado
  const lastMsgSnapshotRef = useRef(null);
  const pollRef = useRef(null);
  const sseRef = useRef(null);
  const teamIdsRef = useRef([]); // IDs dos times do usuário
  const refreshDebounceRef = useRef(null);

  const refresh = useCallback(async () => {
    if (CHATWOOT_MOCK) {
      setConversations(MOCK_CONVERSATIONS);
      const total = MOCK_CONVERSATIONS.mine.reduce((s, c) => s + (c.unread_count || 0), 0);
      setUnreadCount(total);
      return;
    }

    const { chatwoot_token: token, chatwoot_agent_id: agentId } = getUserData();
    if (!token) return;

    setIsLoading(true);
    try {
      const [pending, open, rawTeam] = await Promise.all([
        fetchByStatus('pending', token),
        fetchByStatus('open', token),
        // fetchByStatus('resolved', token), // Resolvidas sem uso no momento
        fetchTeamConvs(teamIdsRef.current, token),
      ]);

      // Minhas Conversas: apenas as efetivamente atribuídas a este agente
      // assignee_type=mine do Chatwoot inclui fila do time (sem assignee) — filtramos aqui
      const mine = [...pending, ...open].filter(
        c => agentId && c.meta?.assignee?.id === agentId
      );

      // Meu Time: todas as conversas dos times do usuário (inclui as atribuídas a ele ou a outros)
      const team = rawTeam;

      setConversations(prev => ({ ...prev, mine, team }));

      // Badge: soma total de unread_count em "Minhas conversas"
      const mineUnread = mine.reduce((s, c) => s + (c.unread_count || 0), 0);
      setUnreadCount(mineUnread);

      // Toast: detectar nova mensagem INCOMING comparando ID por conversa
      const allConvs = [...mine, ...team];
      const snapshot = lastMsgSnapshotRef.current;

      if (snapshot !== null) {
        const changed = allConvs.find(c => {
          const lastMsg = c.last_non_activity_message;
          if (!lastMsg?.id) return false;
          // só dispara para mensagens do cliente (incoming = 0)
          if (lastMsg.message_type !== 0) return false;
          return snapshot[c.id] === undefined || lastMsg.id !== snapshot[c.id];
        });
        if (changed) {
          setNewMessage({
            conversationId: changed.id,
            contact: changed.meta?.sender?.name || 'Nova mensagem',
            preview: changed.last_non_activity_message?.content || '',
          });
        }
      }

      // Atualiza snapshot com estado atual (registra qualquer tipo de msg)
      const newSnap = {};
      allConvs.forEach(c => { newSnap[c.id] = c.last_non_activity_message?.id ?? null; });
      lastMsgSnapshotRef.current = newSnap;

    } catch (e) {
      console.warn('[Chatwoot] Erro ao carregar conversas:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    if (CHATWOOT_MOCK) return MOCK_MESSAGES_DATA[conversationId] || [];

    const token = getUserData().chatwoot_token;
    if (!token) return [];
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/messages`,
      { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
    );
    if (!res.ok) return [];
    const json = await res.json();
    return (json.payload || []).sort((a, b) => a.created_at - b.created_at);
  }, []);

  const sendMessage = useCallback(async (conversationId, content) => {
    if (CHATWOOT_MOCK) {
      const newMsg = {
        id: Date.now(), content, message_type: 1,
        created_at: Math.floor(Date.now() / 1000),
        sender: { name: 'Você' },
      };
      if (!MOCK_MESSAGES_DATA[conversationId]) MOCK_MESSAGES_DATA[conversationId] = [];
      MOCK_MESSAGES_DATA[conversationId].push(newMsg);
      return newMsg;
    }

    const token = getUserData().chatwoot_token;
    if (!token) return null;
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ content, message_type: 'outgoing', private: false }),
      }
    );
    if (!res.ok) return null;
    return res.json();
  }, []);

  const markAsRead = useCallback(async (conversationId) => {
    const token = getUserData().chatwoot_token;
    if (!token) return;
    fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/update_last_seen`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ agent_last_seen_at: Math.floor(Date.now() / 1000) }),
      }
    ).catch(() => {});
  }, []);

  const resolveConversation = useCallback(async (conversationId) => {
    const token = getUserData().chatwoot_token;
    if (!token) return false;
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/toggle_status`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ status: 'resolved' }),
      }
    );
    if (!res.ok) return false;
    await refresh();
    return true;
  }, [refresh]);

  function connectSSE(agentId) {
    if (sseRef.current) return;
    const teamIds = teamIdsRef.current.join(',');
    const url = `/sse/chatwoot?agent_id=${agentId}${teamIds ? `&team_ids=${teamIds}` : ''}`;
    const es = new EventSource(url);

    es.onopen = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    es.onmessage = (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch { return; }

      if (data.type === 'new_message' || data.type === 'conversation_created') {
        const conversationId = data.conversation?.id;
        if (conversationId) {
          // Sinaliza diretamente para o offcanvas atualizar a conversa aberta (sem refresh)
          setLiveConvUpdate({ conversationId, ts: Date.now() });
        }
        // Atualiza lista (unread counts) com debounce para não spammar se chegar várias msgs
        if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = setTimeout(refresh, 800);
      }

      if (data.type === 'conversation_resolved') {
        if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = setTimeout(refresh, 800);
      }
    };

    es.onerror = () => {
      es.close();
      sseRef.current = null;
      if (!pollRef.current) pollRef.current = setInterval(refresh, POLL_INTERVAL_FALLBACK);
      setTimeout(() => connectSSE(agentId), 10000);
    };

    sseRef.current = es;
  }

  useEffect(() => {
    if (CHATWOOT_MOCK) {
      setHasToken(true);
      refresh();
      pollRef.current = setInterval(refresh, POLL_INTERVAL_FALLBACK);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }

    const { chatwoot_token, chatwoot_agent_id } = getUserData();
    setHasToken(!!chatwoot_token);
    if (!chatwoot_token) return;

    // Availability: usa nosso store próprio (imune ao WebSocket do Chatwoot)
    if (chatwoot_agent_id) {
      const manualStatus = localStorage.getItem('chatwoot_availability_manual');
      if (!manualStatus) {
        // Primeira sessão ou após logout: auto-online no nosso backend
        fetch(`/chatwoot-availability/${chatwoot_agent_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'online' }),
        }).then(r => { if (r.ok) setAgentAvailability('online'); }).catch(() => {});
        // Best-effort Chatwoot também
        if (chatwoot_token) {
          fetch(`${API}/api/v1/profile`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-cw-token': chatwoot_token },
            body: JSON.stringify({ availability: 'online' }),
          }).catch(() => {});
        }
      } else {
        // Respeita status manual (ex: Ausente no almoço) — lê do nosso store
        fetch(`/chatwoot-availability`)
          .then(r => r.ok ? r.json() : {})
          .then(store => {
            const myStatus = store[chatwoot_agent_id] || manualStatus;
            setAgentAvailability(myStatus);
          }).catch(() => setAgentAvailability(manualStatus));
      }
    }

    // Busca times do usuário, carrega conversas e conecta SSE com os team_ids
    fetchMemberTeamIds(chatwoot_token).then(teamIds => {
      teamIdsRef.current = teamIds;
      refresh();
      // SSE primário — passa team_ids para notificação por time
      if (chatwoot_agent_id) connectSSE(chatwoot_agent_id);
      // Sem SSE (agente sem chatwoot_agent_id): polling fallback
      else pollRef.current = setInterval(refresh, POLL_INTERVAL_FALLBACK);
    });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [refresh]);

  // ── Availability (store próprio — independente do WebSocket do Chatwoot) ──────

  const setMyAvailability = useCallback(async (status) => {
    const { chatwoot_token: token, chatwoot_agent_id: agentId } = getUserData();
    if (!agentId) return false;

    // 1. Fonte de verdade: nosso backend (imune ao WebSocket do Chatwoot)
    const res = await fetch(`/chatwoot-availability/${agentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      console.warn('[Chatwoot] PUT /chatwoot-availability falhou:', res.status);
      return false;
    }

    // 2. Best-effort: tenta setar no Chatwoot também (pode ser revertido pelo WebSocket)
    if (token) {
      fetch(`${API}/api/v1/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ availability: status }),
      }).catch(() => {});
    }

    setAgentAvailability(status);
    // Registra mudança manual. online limpa a flag → próximo login auto-onlineia.
    if (status === 'online') {
      localStorage.removeItem('chatwoot_availability_manual');
    } else {
      localStorage.setItem('chatwoot_availability_manual', status);
    }
    return true;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────

  const fetchAgents = useCallback(async () => {
    const token = getUserData().chatwoot_token;
    if (!token) return [];
    const [agentsRes, storeRes] = await Promise.all([
      fetch(`${API}/api/v1/accounts/${ACCOUNT_ID}/agents`,
        { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }),
      fetch('/chatwoot-availability'),
    ]);
    if (!agentsRes.ok) return [];
    const [agentsData, store] = await Promise.all([
      agentsRes.json().catch(() => []),
      storeRes.ok ? storeRes.json().catch(() => ({})) : Promise.resolve({}),
    ]);
    const agents = Array.isArray(agentsData) ? agentsData : [];
    // Nossa store tem precedência sobre o Chatwoot (imune ao WebSocket)
    return agents.filter(a => {
      const ourStatus = store[a.id];          // nosso backend
      const cwStatus = a.availability_status; // Chatwoot (pode estar errado se webapp aberta)
      const effective = ourStatus || cwStatus || 'offline';
      return effective === 'online';
    });
  }, []);

  const fetchAllTeams = useCallback(async () => {
    const token = getUserData().chatwoot_token;
    if (!token) return [];
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/teams`,
      { headers: { 'Content-Type': 'application/json', 'x-cw-token': token } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);

  const assignAgent = useCallback(async (conversationId, agentId) => {
    const token = getUserData().chatwoot_token;
    if (!token) return false;

    // 1. Atribuir agente
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/assignments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ assignee_id: agentId }),
      }
    );
    if (!res.ok) return false;

    // 2. Atribuir o primeiro time do agente (best-effort)
    try {
      const teamId = await fetchAgentFirstTeamId(agentId, token);
      if (teamId) {
        await fetch(
          `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
            body: JSON.stringify({ team_id: teamId }),
          }
        );
      }
    } catch (e) {
      console.warn('[Chatwoot] Não foi possível atribuir time do agente:', e);
    }

    await refresh();
    return true;
  }, [refresh]);

  const assignTeam = useCallback(async (conversationId, teamId) => {
    const token = getUserData().chatwoot_token;
    if (!token) return false;

    // 1. Atribuir time
    const res = await fetch(
      `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
        body: JSON.stringify({ team_id: teamId }),
      }
    );
    if (!res.ok) return false;

    // 2. Sortear agente online do time e atribuir (best-effort)
    try {
      const [members, storeRes] = await Promise.all([
        fetchTeamMembers(teamId, token),
        fetch('/chatwoot-availability'),
      ]);
      const store = storeRes.ok ? await storeRes.json().catch(() => ({})) : {};
      const online = members.filter(m => {
        const effective = store[m.id] || m.availability_status || 'offline';
        return effective === 'online';
      });
      if (online.length > 0) {
        const randomAgent = online[Math.floor(Math.random() * online.length)];
        await fetch(
          `${API}/api/v1/accounts/${ACCOUNT_ID}/conversations/${conversationId}/assignments`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-cw-token': token },
            body: JSON.stringify({ assignee_id: randomAgent.id }),
          }
        );
      }
      // Sem agentes online → fica só com o time atribuído (fila do time)
    } catch (e) {
      console.warn('[Chatwoot] Não foi possível atribuir agente aleatório do time:', e);
    }

    await refresh();
    return true;
  }, [refresh]);

  const dismissToast = useCallback(() => setNewMessage(null), []);

  return {
    conversations,
    unreadCount,
    isLoading,
    hasToken,
    newMessage,
    liveConvUpdate,
    agentAvailability,
    setMyAvailability,
    refresh,
    fetchMessages,
    sendMessage,
    resolveConversation,
    markAsRead,
    fetchAgents,
    fetchAllTeams,
    assignAgent,
    assignTeam,
    dismissToast,
    chatwootBase: CHATWOOT_BASE,
    accountId: ACCOUNT_ID,
  };
}
